import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@ais-ui/ui-core';
import { LoginFormValues } from '@ais-ui/auth';
import { useLocalization } from '@ais-ui/locale-management';

interface MultiFactorDialogProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  onCredentialsLogin: (
    data: LoginFormValues,
    totpCode?: number
  ) => Promise<void>;
  loginError: string | null;
  credentials: { username: string; password: string };
}

export const MultiFactorTOTPDialog = ({
  showDialog,
  setShowDialog,
  onCredentialsLogin,
  loginError,
  credentials,
}: MultiFactorDialogProps) => {
  const { messages } = useLocalization();
  const [otp, setOtp] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Filter out non-numeric characters and limit to 6 digits
  const handleOtpChange = useCallback((value: string) => {
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setOtp(numericValue);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    // Validate that OTP is exactly 6 digits
    if (!/^\d{6}$/.test(otp)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const otpNumber = parseInt(otp, 10);
      await onCredentialsLogin(
        {
          username: credentials.username,
          password: credentials.password,
        },
        otpNumber
      );
    } catch (error) {
      console.error('MFA submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [otp, credentials, onCredentialsLogin, isSubmitting]);

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    if (otp.length === 6 && !isSubmitting && /^\d{6}$/.test(otp)) {
      handleSubmit();
    }
  }, [otp, isSubmitting, handleSubmit]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!showDialog) {
      setOtp('');
      setIsSubmitting(false);
    }
  }, [showDialog]);

  // Clear OTP on login error
  useEffect(() => {
    if (loginError) {
      setOtp('');
      setIsSubmitting(false);
    }
  }, [loginError]);

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.multiFactorDialogTtopTitle}</DialogTitle>
        </DialogHeader>
        <InputOTP
          maxLength={6}
          value={otp}
          onChange={handleOtpChange}
          pattern="[0-9]*" // Mobile numeric keyboard
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
        {loginError && (
          <div className="text-red-600 p-4 rounded-lg border border-red-300 bg-red-50">
            <p className="font-medium">
              {messages.multiFactorDialogInvalidTtopTitle}
            </p>
            <p className="text-sm mt-1">
              {messages.multiFactorDialogInvalidTtopDescription}
            </p>
          </div>
        )}
        <p className="text-sm">{messages.multiFactorDialogTtopDescription}</p>
        <DialogFooter className="flex justify-end w-full">
          <Button
            onClick={handleSubmit}
            disabled={otp.length !== 6 || isSubmitting || !/^\d{6}$/.test(otp)}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
